import React from 'react';
import type { WorkflowState } from '../../services/auditService';

/**
 * Style-based status badge for the auditor staff portal.
 *
 * Plan 05a-02 (STAFF-02): the existing `AuditShared.tsx::StatusBadge` uses
 * Tailwind color utilities (`bg-blue-100`, `text-orange-700`, ...) which the
 * phase 04 W1 carve-out bans in new code. This module is the staff-portal
 * replacement — pure inline styles, ready for reuse by 05b's approval surface.
 *
 * Keyed off WorkflowState (the canonical backend enum from auditService).
 * Sizes: 'sm' is the dashboard card variant, 'md' the default.
 */

export interface StatusStyle {
  bg: string;
  color: string;
  dot: string;
}

export const WORKFLOW_STATE_STYLE: Record<WorkflowState, StatusStyle> = {
  Draft:          { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' },
  Submitted:      { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  InReview:       { bg: '#e0f2fe', color: '#0369a1', dot: '#0284c7' },
  Approved:       { bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  Rejected:       { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  Reopened:       { bg: '#fff7ed', color: '#9a3412', dot: '#f97316' },
  // Phase 06 (FIND-01..04) — finding lifecycle (D2 + D3)
  Raised:         { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  Responded:      { bg: '#e0f2fe', color: '#0369a1', dot: '#0284c7' },
  Closed:         { bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  // Phase 106 / plan 05 — D-07 program lifecycle states
  Launched:       { bg: '#ede9fe', color: '#5b21b6', dot: '#7c3aed' },
  Allocated:      { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  Active:         { bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  PendingApproval:{ bg: '#fef9c3', color: '#713f12', dot: '#eab308' },
  DueForRelease:  { bg: '#ffedd5', color: '#9a3412', dot: '#f97316' },
  Released:       { bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
  InProgress:     { bg: '#e0f2fe', color: '#0369a1', dot: '#38bdf8' },
};

const FALLBACK: StatusStyle = { bg: '#f9fafb', color: '#6b7280', dot: '#d1d5db' };

interface AuditStatusBadgeProps {
  state: WorkflowState | string;
  size?: 'sm' | 'md';
}

export const AuditStatusBadge: React.FC<AuditStatusBadgeProps> = ({ state, size = 'md' }) => {
  const s = (WORKFLOW_STATE_STYLE as Record<string, StatusStyle>)[state] ?? FALLBACK;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: size === 'sm' ? '1px 7px' : '2px 9px',
        borderRadius: 12,
        fontSize: size === 'sm' ? 10 : 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: s.dot,
          flexShrink: 0,
        }}
      />
      {state}
    </span>
  );
};

export default AuditStatusBadge;
